import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { useMemo, type ReactNode } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import {
  Auth0ProviderWithRouter,
  RequireAuth,
  RequireVerifiedEmail,
  useAuthTokenWiring,
} from '@my/platform/auth';
import { directoryApi } from '@my/directory-api';
import { ToastContainer } from '@my/ui';
import { ShieldCheck } from 'lucide-react';
import { RequireCapability } from '@/auth/RequireCapability';
import { getConfig } from '@/configuration/config';
import { AppShell } from '@/layout/AppShell';
import { HomePage } from '@/features/home/HomePage';
import { CheckListPage } from '@/features/checks/CheckListPage';
import { CheckDetailPage } from '@/features/checks/CheckDetailPage';
import { DefectListPage } from '@/features/defects/DefectListPage';
import { TemplateListPage } from '@/features/templates/TemplateListPage';
import { OrganisationPage } from '@/features/settings/organisation/OrganisationPage';
import { ResourceGridPage } from '@/features/settings/resources/ResourceGridPage';
import { UsersAndAccessPage } from '@/features/settings/users-and-access/UsersAndAccessPage';
import { resolveBootLanguage } from '@/localisation';
import { Diagnostics } from '@/pages/Diagnostics';
import { NotFound } from '@/pages/NotFound';

/**
 * Bridges Auth0 → axios. Has to live INSIDE Auth0Provider so the hook can read
 * its context. Renders nothing.
 */
function AuthTokenBridge() {
  useAuthTokenWiring();
  return null;
}

export function App() {
  const cfg = getConfig();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            staleTime: 30_000,
          },
        },
      }),
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <MaybeMapsProvider apiKey={cfg.googleMapsApiKey}>
        <BrowserRouter>
          <Auth0ProviderWithRouter
            settings={{
              domain: cfg.a0Domain,
              clientId: cfg.a0ClientId,
              audience: cfg.a0Audience,
              subscription: 'check',
              extraAuthorizationParams: { ui_locales: resolveBootLanguage() },
            }}
          >
            <AuthTokenBridge />
            <Routes>
              {/* Authenticated app shell — every screen renders inside <AppShell />. */}
              <Route
                element={
                  <RequireAuth brand={{ title: 'My Check', icon: ShieldCheck }}>
                    {/* Email must be confirmed before any screen renders —
                        self-signup users arrive unverified. */}
                    <RequireVerifiedEmail
                      brand={{ title: 'My Check', icon: ShieldCheck }}
                      onResend={() => directoryApi.resendMyVerificationEmail()}
                    >
                      <AppShell />
                    </RequireVerifiedEmail>
                  </RequireAuth>
                }
              >
                <Route index element={<HomePage />} />
                <Route path="dashboard" element={<HomePage />} />

                {/*
                  Recorded checks — read-only. A check is created by a driver in
                  myDriver, never from the web, so there is no "new" route here.
                  Any authenticated Check user can read them.
                */}
                <Route path="checks" element={<CheckListPage />} />
                <Route path="checks/:id" element={<CheckDetailPage />} />
                {/* Defects are raised by a check rather than created here —
                    this is the fleet-wide triage view over them. */}
                <Route path="defects" element={<DefectListPage />} />
                <Route path="templates" element={<TemplateListPage />} />

                {/*
                  Settings tree, gated per page on the server-computed
                  capability list rather than on role literals — the same
                  question the sidebar asks, so a page cannot be reachable
                  while its menu item is hidden.

                  Per page, not one wrapper for the tree: a Manager gets
                  Vehicles, Drivers and Users & Access but not Organisation,
                  which a single `roles={['owner','admin']}` guard could not
                  express. See `AppCapabilities.CheckManagerSettings`.
                */}
                <Route element={<RequireCapability anyOf={['settings.organisation']} />}>
                  <Route path="settings/organisation" element={<OrganisationPage />} />
                </Route>
                <Route element={<RequireCapability anyOf={['settings.vehicles', 'settings.drivers']} />}>
                  {/* Resources are shared master data owned by My.Directory —
                      the same rows TMS and Depot edit, reached here through
                      @my/directory-api rather than the Check API.

                      Split in two because the columns and actions diverge:
                      vehicles carry a check template, drivers carry app access,
                      and "Last Check" means received vs performed. One
                      component, two modes — the grid is already a TMS fork and
                      a third copy would mean fixing everything three times. */}
                  <Route
                    path="settings/vehicles"
                    element={<ResourceGridPage mode="vehicles" />}
                  />
                  <Route
                    path="settings/drivers"
                    element={<ResourceGridPage mode="drivers" />}
                  />
                  {/* Back-compat for anything still pointing at the old path. */}
                  <Route
                    path="settings/resources"
                    element={<Navigate to="/settings/vehicles" replace />}
                  />
                </Route>
                <Route element={<RequireCapability anyOf={['settings.usersAndAccess']} />}>
                  <Route path="settings/users-and-access" element={<UsersAndAccessPage />} />
                </Route>

                <Route path="_diagnostics" element={<Diagnostics />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Auth0ProviderWithRouter>
        </BrowserRouter>
      </MaybeMapsProvider>
      <ToastContainer />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

/**
 * Wraps children in `<APIProvider>` only when an API key is configured. The
 * google-maps SDK lazily loads on the first `useMapsLibrary(...)` call inside;
 * a missing key just means map widgets render in a degraded state rather than
 * blowing up the whole app at boot.
 */
function MaybeMapsProvider({ apiKey, children }: { apiKey?: string; children: ReactNode }) {
  if (!apiKey) return <>{children}</>;
  return (
    <APIProvider apiKey={apiKey} libraries={['places']}>
      {children}
    </APIProvider>
  );
}
